import { DataTable } from '@gastrosoft/shared/ui';
import {
  SwissbitUsageReportDocument,
  type SwissbitTseReconciliationStatus,
  type SwissbitTseVendorStatus,
  type SwissbitUsageReportQuery,
} from '@/generated/pos-types';
import { posStaticContext } from '@/graphql/client';
import { subscriptionStateColors, translateSubscriptionState } from '@/utils/billingStateLabels';
import CircleIcon from '@mui/icons-material/Circle';
import { useQuery } from '@apollo/client/react';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const MIN_PERIOD = '2026-01';
const VENDOR_STATUSES: SwissbitTseVendorStatus[] = ['CREATED', 'ACTIVE', 'DEACTIVATED'];

/** Terminal vendor status wins when the same TSE appears in multiple period buckets. */
const VENDOR_STATUS_PRIORITY: Record<SwissbitTseVendorStatus, number> = {
  DEACTIVATED: 2,
  ACTIVE: 1,
  CREATED: 0,
};

type TseRow = NonNullable<
  SwissbitUsageReportQuery['swissbitUsageReport']
>['createdTses'][number] & {
  id: string;
};

type Filter =
  | { field: 'vendorStatus'; value: SwissbitTseVendorStatus }
  | { field: 'reconciliationStatus'; value: SwissbitTseReconciliationStatus }
  | null;

const vendorStatusColor: Record<SwissbitTseVendorStatus, string> = {
  CREATED: 'info.main',
  ACTIVE: 'success.main',
  DEACTIVATED: 'error.main',
};

const reconciliationChipColor: Record<
  SwissbitTseReconciliationStatus,
  'success' | 'warning' | 'error' | 'default'
> = {
  NONE: 'default',
  OK: 'success',
  CANCELLED: 'warning',
  PROBLEM: 'error',
  UNMATCHED: 'error',
};

function StatusLabel({ status, label }: { status: SwissbitTseVendorStatus; label: string }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <CircleIcon sx={{ color: vendorStatusColor[status], fontSize: 12 }} />
      {label}
    </Box>
  );
}

function buildUsageRows(
  usage: SwissbitUsageReportQuery['swissbitUsageReport'] | undefined,
): TseRow[] {
  return [
    ...(usage?.createdTses ?? []).map((r) => ({ ...r, id: `C-${r.tseSerialNumber}` })),
    ...(usage?.activeTses ?? []).map((r) => ({ ...r, id: `A-${r.tseSerialNumber}` })),
    ...(usage?.deactivatedTses ?? []).map((r) => ({ ...r, id: `D-${r.tseSerialNumber}` })),
  ];
}

function dedupeRowsBySerial(rows: TseRow[]): TseRow[] {
  const bySerial = new Map<string, TseRow>();

  for (const row of rows) {
    const existing = bySerial.get(row.tseSerialNumber);
    if (
      !existing ||
      VENDOR_STATUS_PRIORITY[row.vendorStatus] > VENDOR_STATUS_PRIORITY[existing.vendorStatus]
    ) {
      bySerial.set(row.tseSerialNumber, { ...row, id: row.tseSerialNumber });
    }
  }

  return [...bySerial.values()];
}

export default function SecurityDevices() {
  const { t, i18n } = useTranslation();
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(i18n.language) : '—';
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('rowsPerPage');
    return saved ? parseInt(saved, 10) : 25;
  });
  const [filter, setFilter] = useState<Filter>(null);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const periodValue = `${year}-${String(month).padStart(2, '0')}`;

  const handlePeriodChange = (value: string) => {
    if (!value) return;
    const [nextYear, nextMonth] = value.split('-').map(Number);
    if (!nextYear || !nextMonth) return;
    setYear(nextYear);
    setMonth(nextMonth);
    setPage(0);
  };

  const { data, loading } = useQuery(SwissbitUsageReportDocument, {
    variables: { month, year },
    context: posStaticContext,
    notifyOnNetworkStatusChange: true,
  });

  const allRows = buildUsageRows(data?.swissbitUsageReport);

  const filteredRows =
    filter === null
      ? dedupeRowsBySerial(allRows)
      : allRows.filter((r) => r[filter.field] === filter.value);

  const rows = [...filteredRows].sort((a, b) => {
    if (a.reconciliationStatus === 'PROBLEM' && b.reconciliationStatus !== 'PROBLEM') return -1;
    if (a.reconciliationStatus !== 'PROBLEM' && b.reconciliationStatus === 'PROBLEM') return 1;
    return a.tseSerialNumber.localeCompare(b.tseSerialNumber);
  });

  const getVendorLabel = (status: SwissbitTseVendorStatus) =>
    t(`securityDevices.vendorStatus.${status}`);

  const getReconciliationLabel = (status: SwissbitTseReconciliationStatus) =>
    t(`securityDevices.reconciliation.${status}`);

  const formatExpiry = (row: TseRow) => {
    if (!row.subscriptionExpiryDate) return '—';
    const dateLabel = formatDate(row.subscriptionExpiryDate);
    if (row.daysUntilExpiry == null) return dateLabel;
    return t('securityDevices.expiresIn', { date: dateLabel, count: row.daysUntilExpiry });
  };

  const filterKey = filter === null ? '' : `${filter.field}:${filter.value}`;

  const columns: GridColDef[] = [
    {
      field: 'tseSerialNumber',
      headerName: t('securityDevices.serialNumber'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'organizationUnitName',
      headerName: t('securityDevices.organizationUnit'),
      flex: 1,
      minWidth: 160,
      valueFormatter: (value) => (value as string | null) ?? '—',
    },
    { field: 'productType', headerName: t('securityDevices.productType'), flex: 1, minWidth: 140 },
    { field: 'autoTopUps', headerName: t('securityDevices.autoTopUps'), flex: 0.8, minWidth: 120 },
    {
      field: 'vendorStatus',
      headerName: t('securityDevices.status'),
      flex: 0.9,
      minWidth: 150,
      renderCell: ({ row }) => (
        <StatusLabel
          status={(row as TseRow).vendorStatus}
          label={getVendorLabel((row as TseRow).vendorStatus)}
        />
      ),
      valueFormatter: (value) => getVendorLabel(value as SwissbitTseVendorStatus),
    },
    {
      field: 'reconciliationStatus',
      headerName: t('securityDevices.reconciliationStatus'),
      flex: 1,
      minWidth: 160,
      renderCell: ({ row }) => {
        const status = (row as TseRow).reconciliationStatus;
        return (
          <Chip
            size="small"
            color={reconciliationChipColor[status]}
            label={getReconciliationLabel(status)}
          />
        );
      },
      valueFormatter: (value) => getReconciliationLabel(value as SwissbitTseReconciliationStatus),
    },
    {
      field: 'subscriptionNumber',
      headerName: t('securityDevices.subscriptionNumber'),
      flex: 0.9,
      minWidth: 140,
      valueFormatter: (value) => (value as string | null) ?? '—',
    },
    {
      field: 'subscriptionState',
      headerName: t('securityDevices.subscriptionState'),
      flex: 0.9,
      minWidth: 140,
      renderCell: ({ row }) => {
        const state = (row as TseRow).subscriptionState;
        if (!state) return '—';
        return (
          <Chip
            size="small"
            color={subscriptionStateColors[state] ?? 'default'}
            label={translateSubscriptionState(t, state)}
          />
        );
      },
      valueFormatter: (value) => (value ? translateSubscriptionState(t, value as string) : '—'),
    },
    {
      field: 'subscriptionExpiryDate',
      headerName: t('securityDevices.subscriptionExpiry'),
      flex: 1.1,
      minWidth: 180,
      valueGetter: (_value, row) => formatExpiry(row as TseRow),
    },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h4">{t('securityDevices.title')}</Typography>
        <TextField
          label={t('securityDevices.period')}
          type="month"
          value={periodValue}
          onChange={(e) => handlePeriodChange(e.target.value)}
          slotProps={{
            inputLabel: { shrink: true },
            htmlInput: { min: MIN_PERIOD },
          }}
          size="small"
          sx={{ minWidth: 180 }}
        />
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="securityDevices-filter-label">{t('securityDevices.filter')}</InputLabel>
          <Select
            labelId="securityDevices-filter-label"
            label={t('securityDevices.filter')}
            value={filterKey}
            onChange={(e) => {
              const key = e.target.value as string;
              if (!key) {
                setFilter(null);
              } else {
                const sep = key.indexOf(':');
                const field = key.slice(0, sep) as 'vendorStatus' | 'reconciliationStatus';
                const value = key.slice(sep + 1);
                setFilter({ field, value } as Filter & object);
              }
              setPage(0);
            }}
            renderValue={(value) => {
              if (!value) return t('securityDevices.all');
              if (value === 'reconciliationStatus:PROBLEM') return t('securityDevices.billingRisk');
              const status = value.split(':')[1] as SwissbitTseVendorStatus;
              return <StatusLabel status={status} label={getVendorLabel(status)} />;
            }}
          >
            <MenuItem value="">{t('securityDevices.all')}</MenuItem>
            <MenuItem value="reconciliationStatus:PROBLEM">
              {t('securityDevices.billingRisk')}
            </MenuItem>
            {VENDOR_STATUSES.map((status) => (
              <MenuItem key={status} value={`vendorStatus:${status}`}>
                <StatusLabel status={status} label={getVendorLabel(status)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable<TseRow>
        enableMobileCards
        columns={columns}
        data={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={rows.length}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        paginationMode="client"
      />
    </Box>
  );
}
